import {
  useAiAssistMutation,
  useCreateChartTemplateMutation,
  useCreateModificationMutation,
  useCreateSpreadsheetMutation,
  useDeleteChartTemplateMutation,
  useDeleteModificationMutation,
  useLazyGetAllChartTemplatesQuery,
  useLazyGetDraftQuery,
  useLazyGetSpreadsheetValuesQuery,
  useLazyGetTableCraftColumnEnrichmentQuery,
  useLazyGetTableCraftFunctionsQuery,
  useLazyGetTableCraftObjectsQuery,
  useLazyGetTableCraftRowsQuery,
  useLazySearchTransactionsQuery,
  useRecorderSessionRefreshMutation,
  useReorderModificationMutation,
  useRevertTableCraftModificationMutation,
  useUpdateChartTemplateMutation,
  useUpdateModificationMutation,
  useUpdateTableCraftColumnMutation,
  useUpdateTableCraftRowMutation,
  useUpdateTableCraftSettingsMutation,
  useUploadCodeSnippetMutation,
  useUploadImageMutation,
  useUploadTransactionRepresentationFieldsMutation,
  useUploadTransactionResponseContentMutation,
  useVisualTableCraftSearchMutation,
} from '@carboncopyco/data';
import type { EditorSDK } from '@carboncopyco/editor-sdk';
import type { Modification } from '@carboncopyco/types';
import type { IGlobalTab } from '@carboncopyco/types/desktop';
import { useMemo } from 'react';
import debuggerInterceptorPathListState from '../../shared/utils/debugger-interceptor-path-list-state';

export interface UseEditorApiProps {
  organizationId: string;
  demoId: string;
  draftId: string;
  tab: IGlobalTab;
  getModifications: () => Promise<Modification[]>;
}

export const useEditorApi = ({
  organizationId,
  demoId,
  draftId,
  tab,
  getModifications,
}: UseEditorApiProps) => {
  const [createModification] = useCreateModificationMutation();
  const [updateModification] = useUpdateModificationMutation();
  const [deleteModification] = useDeleteModificationMutation();
  const [reorderModification] = useReorderModificationMutation();
  const [uploadImage] = useUploadImageMutation();
  const [uploadCodeSnippet] = useUploadCodeSnippetMutation();
  const [uploadTransactionResponseContent] =
    useUploadTransactionResponseContentMutation();
  const [uploadTransactionRepresentationFields] =
    useUploadTransactionRepresentationFieldsMutation();
  const [deleteChartTemplate] = useDeleteChartTemplateMutation();
  const [createTemplate] = useCreateChartTemplateMutation();
  const [updateTemplate] = useUpdateChartTemplateMutation();
  const [getAllChartTemplates] = useLazyGetAllChartTemplatesQuery();
  const [recorderRefreshSession] = useRecorderSessionRefreshMutation();
  const [createSpreadsheet] = useCreateSpreadsheetMutation();
  const [updateTableCraftRow] = useUpdateTableCraftRowMutation();
  const [updateTableCraftColumn] = useUpdateTableCraftColumnMutation();
  const [updateTableCraftSettings] = useUpdateTableCraftSettingsMutation();
  const [getSpreadsheetValues] = useLazyGetSpreadsheetValuesQuery();
  const [searchTransactions] = useLazySearchTransactionsQuery();
  const [getTableCraftObjects] = useLazyGetTableCraftObjectsQuery();
  const [getTableCraftRows] = useLazyGetTableCraftRowsQuery();
  const [visualTableCraftSearch] = useVisualTableCraftSearchMutation();
  const [revertTableCraftModification] =
    useRevertTableCraftModificationMutation();
  const [getDraft] = useLazyGetDraftQuery();
  const [aiAssist] = useAiAssistMutation();
  const [getTableCraftFunctions] = useLazyGetTableCraftFunctionsQuery();
  const [getTableCraftColumnEnrichment] =
    useLazyGetTableCraftColumnEnrichmentQuery();

  const { updateImageReplacePathListStateModification } =
    debuggerInterceptorPathListState(tab);

  return useMemo<EditorSDK['api']>(
    () => ({
      uploadImage: (fileData: File) =>
        uploadImage({ organizationId, fileData }).unwrap(),

      getChartTemplates: () =>
        getAllChartTemplates({ organizationId }).unwrap(),

      deleteChartTemplate: (chartTemplateId) =>
        deleteChartTemplate({
          organizationId,
          chartId: chartTemplateId,
        }).unwrap(),

      updateChartTemplate: ({ chartId, payload }) =>
        updateTemplate({
          organizationId,
          chartId,
          payload,
        }).unwrap(),

      openUrlLink: async (url) => {
        await window.dsDesktop?.shellOpenExternal(url);
      },

      createChartTemplate: (payload) =>
        createTemplate({
          organizationId,
          payload,
        }).unwrap(),

      recorderSessionRefresh: async (payload) => {
        const modifications = await getModifications();
        updateImageReplacePathListStateModification(modifications);
        const innerTab = tab.innerTabs.find(
          ({ id }) => id === tab.activeInnerTabId
        );
        if (innerTab) {
          await window?.dsDesktop?.interceptor.clearCache(innerTab);
          await window?.dsDesktop?.interceptor.updateRouterParams(
            innerTab,
            modifications
          );
        }
        return recorderRefreshSession({
          ...payload,
          organizationId,
        }).unwrap();
      },

      uploadCodeSnippet: (fileData: File) => {
        return uploadCodeSnippet({
          organizationId,
          snippet: fileData,
        }).unwrap();
      },

      createModification: (data) =>
        createModification({
          organizationId,
          entityType: 'draft',
          entityId: draftId,
          ...data,
        }).unwrap(),

      updateModification: (data: any) =>
        updateModification({
          organizationId,
          entityType: 'draft',
          entityId: draftId,
          ...data,
        }).unwrap(),

      deleteModification: (data: any) =>
        deleteModification({
          organizationId,
          entityType: 'draft',
          entityId: draftId,
          ...data,
        }).unwrap(),

      uploadTransactionResponseContent: (responseContent: any) =>
        uploadTransactionResponseContent({
          organizationId,
          responseContent,
        }).unwrap(),

      uploadTransactionRepresentationFields: (representationFields: any) =>
        uploadTransactionRepresentationFields({
          organizationId,
          representationFields,
        }).unwrap(),

      reorderModification: (data: any) =>
        reorderModification({
          organizationId,
          entityType: 'draft',
          entityId: draftId,
          ...data,
        }).unwrap(),

      createSpreadsheet: ({ title, values }) =>
        createSpreadsheet({
          organizationId,
          draftId,
          title,
          values,
        }).unwrap(),

      getSpreadsheetValues: (spreadsheetId) =>
        getSpreadsheetValues({
          organizationId,
          spreadsheetId,
        }).unwrap(),

      searchTransactions: ({ htmlElementString, referer }) =>
        searchTransactions({
          organizationId,
          entityId: draftId,
          htmlElementString,
          referer,
        }).unwrap(),

      // Because the response is not wrapped in a data key, and we can't use .unwrap().
      // That needs to be fixed in the API
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      updateTableCraftRow: async ({ column, ...rest }) => {
        const response = await updateTableCraftRow({
          organizationId,
          draftId,
          key: column,
          ...rest,
        }).unwrap();
        getDraft({
          organizationId,
          draftId,
        });
        return response;
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      updateTableCraftColumn: async ({ col_name, ...rest }) => {
        const response = await updateTableCraftColumn({
          organizationId,
          draftId,
          col_name,
          ...rest,
        }).unwrap();
        getDraft({
          organizationId,
          draftId,
        });
        return response;
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      updateTableCraftSettings: (data) =>
        updateTableCraftSettings({
          organizationId,
          draftId,
          ...data,
        }),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      revertTableCraftModification: async (data) => {
        const response = await revertTableCraftModification({
          organizationId,
          draftId,
          ...data,
        }).unwrap();
        getDraft({
          organizationId,
          draftId,
        });
        return response;
      },

      visualTableCraftSearch: ({ htmlElementString, referer }) =>
        visualTableCraftSearch({
          organizationId,
          draftId,
          htmlElementString,
          referer,
        }).unwrap(),

      getTableCraftObjects: (data) =>
        getTableCraftObjects({
          organizationId,
          draftId,
          searchQuery: data?.searchQuery,
          page: data?.page,
          size: data?.size,
        }).unwrap(),

      getTableCraftRows: (objectId: string, data) =>
        getTableCraftRows({
          organizationId,
          draftId,
          objectId,
          searchQuery: data?.searchQuery,
          page: data?.page,
          size: data?.size,
        }).unwrap(),

      getTableCraftFunctions: () => getTableCraftFunctions().unwrap(),

      aiAssist: async (data) => {
        const response = await aiAssist({
          organizationId,
          ...data,
        }).unwrap();
        getDraft({
          organizationId,
          draftId,
        });
        return response;
      },

      getTableCraftEnrichmentColumns: (data) =>
        getTableCraftColumnEnrichment({
          organizationId,
          draftId,
          ...data,
        }).unwrap(),
    }),
    [organizationId, demoId, draftId]
  );
};
